import { AnimatePresence, motion } from 'framer-motion'
import { edgeKey, partitionKComplex } from '../engine/ku'
import type {
  CompiledKMachine,
  KExecutionState,
  KLayout,
  KTypeId,
} from '../engine/types'

interface GraphRendererProps {
  state: KExecutionState
  machine: CompiledKMachine
  layout: KLayout
  reducedMotion: boolean
  height?: number
}

function displayType(type: KTypeId): string {
  if (type.startsWith('VAL_')) return type.slice(4).toLowerCase()
  if (type === 'BIT_L') return 'L'
  if (type === 'BIT_R') return 'R'
  return type
}

function neighborCount(state: KExecutionState, id: string): number {
  return state.complex.edges.filter((edge) => edge.a === id || edge.b === id).length
}

export function GraphRenderer({
  state,
  machine,
  layout,
  reducedMotion,
  height = 390,
}: GraphRendererProps) {
  const partition = partitionKComplex(
    state.complex,
    machine.source.signature,
    machine.source.radius,
  )
  const activeVertices = new Set(partition.activeVertices)
  const frontierVertices = new Set(partition.frontierVertices)
  const activeEdges = new Set(partition.activeEdges.map(edgeKey))
  const createdVertices = new Set(state.lastTrace?.createdVertices ?? [])
  const createdEdges = new Set((state.lastTrace?.createdEdges ?? []).map(edgeKey))
  const description = [
    `K-machine state after ${state.step} replacement${state.step === 1 ? '' : 's'}.`,
    `${state.complex.vertices.length} vertices and ${state.complex.edges.length} edges.`,
    `${partition.activeVertices.length} vertices are in U; ${partition.frontierVertices.length} are in frontier L.`,
    `Outcome: ${state.outcome}.`,
  ].join(' ')

  return (
    <div className="km-graph-wrap">
      <svg
        role="img"
        aria-label={description}
        width="100%"
        height={height}
        viewBox="0 0 820 390"
        preserveAspectRatio="xMidYMid meet"
        className="km-graph"
      >
        <desc>{description}</desc>

        <AnimatePresence>
          {state.complex.edges.map((edge) => {
            const a = layout[edge.a]
            const b = layout[edge.b]
            if (!a || !b) return null
            const active = activeEdges.has(edgeKey(edge))
            const created = createdEdges.has(edgeKey(edge))
            return (
              <motion.line
                key={edgeKey(edge)}
                x1={a.x}
                y1={a.y}
                x2={b.x}
                y2={b.y}
                className={active ? 'km-edge km-edge-active' : 'km-edge km-edge-external'}
                initial={reducedMotion ? false : { opacity: 0 }}
                animate={{ opacity: active ? 0.9 : 0.34 }}
                exit={reducedMotion ? undefined : { opacity: 0 }}
                transition={{ duration: reducedMotion ? 0 : created ? 0.32 : 0.18 }}
              />
            )
          })}
        </AnimatePresence>

        <AnimatePresence>
          {state.complex.vertices.map((vertex) => {
            const point = layout[vertex.id]
            if (!point) return null
            const active = activeVertices.has(vertex.id)
            const frontier = frontierVertices.has(vertex.id)
            const initial =
              vertex.type === machine.source.signature.workingInitialType
              || vertex.type === machine.source.signature.finalInitialType
            const detachedBit =
              vertex.type.startsWith('BIT_') && neighborCount(state, vertex.id) === 0
            const created = createdVertices.has(vertex.id)

            return (
              <motion.g
                key={vertex.id}
                initial={reducedMotion ? false : { opacity: 0 }}
                animate={{ opacity: detachedBit ? 0.42 : active ? 1 : 0.72 }}
                exit={reducedMotion ? undefined : { opacity: 0 }}
                transition={{ duration: reducedMotion ? 0 : created ? 0.34 : 0.2 }}
                data-vertex-id={vertex.id}
                data-vertex-type={vertex.type}
              >
                {/* Translation lives on a plain <g>: framer-motion owns the CSS
                    transform of its own element, which overrides SVG transform
                    attributes and would pin every vertex to the origin. */}
                <g transform={`translate(${point.x} ${point.y})`}>
                  {frontier ? <circle r="26" className="km-frontier-halo" /> : null}
                  <circle
                    r={initial ? 20 : 18}
                    className={active ? 'km-node km-node-active' : 'km-node km-node-external'}
                  />
                  {initial ? <circle r="15" className="km-initial-ring" /> : null}
                  {created ? <circle r="23" className="km-created-ring" /> : null}
                  <text className="km-node-label" textAnchor="middle" dominantBaseline="central">
                    {displayType(vertex.type)}
                  </text>
                  {detachedBit ? (
                    <text className="km-detached-mark" textAnchor="middle" y="32">
                      detached
                    </text>
                  ) : null}
                  <title>{`${vertex.id}: ${vertex.type}${frontier ? ' — frontier L' : active ? ' — active U' : ' — external V'}`}</title>
                </g>
              </motion.g>
            )
          })}
        </AnimatePresence>
      </svg>

      <div className="km-legend" aria-hidden="true">
        <span><i className="km-legend-initial" /> T0/T1 initial vertex</span>
        <span><i className="km-legend-active" /> active part U</span>
        <span><i className="km-legend-frontier" /> frontier L</span>
        <span><i className="km-legend-external" /> external part V</span>
      </div>
    </div>
  )
}
